import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../dataContext";
import phone from "phone";
import useApi from "../api/api";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import { getOS, isMobile } from "../utils/utils";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RecipientTypeSelector from "../components/RecipientTypeSelector";
import { SpinnerCircularFixed } from "spinners-react";
import ConfirmTransferModal from "../components/ConfirmTransferModal";

const SendMoney = ({
  dropDownStyle,
  dropDownTheme,
  setFromDemoPage,
  isScheduled,
  branding,
}) => {
  const { TRANSFER_PROCESSING, SCHEDULE_PAYMENT, SCHEDULED_DETAILS } =
    ROUTE_CONSTANTS;
  const {
    accounts,
    cards,
    loggedIn,
    scheduledAlert,
    setScheduledAlert,
    getScheduledTransfers,
    p2pConfig,
    initialDataLoading,
  } = useContext(DataContext);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const api = useApi(false);
  const schedulerApi = useApi(true);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fromAccount, setFromAccount] = useState(undefined);
  const [disableSendToPhone, setDisableSendToPhone] = useState(true);

  // Draft persistence. Navigating to History / Settings unmounts this screen,
  // which used to throw away everything typed. The in-progress form is kept in
  // sessionStorage (this tab only, cleared when the tab closes) and dropped
  // once a transfer or request is actually submitted. Deep-link params still
  // win over a stored draft.
  const draftKey = isScheduled ? "np_send_draft_scheduled" : "np_send_draft";
  const draft = useMemo(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem(draftKey)) || {};
    } catch (e) {
      return {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clearDraft = () => {
    try {
      window.sessionStorage.removeItem(draftKey);
    } catch (e) {
      // storage unavailable — nothing to clear
    }
  };

  const recipientLooksValid = (value) =>
    (value?.match(/^[0-9, +, \-, (, )]+$/) != null && value.length > 9) ||
    value
      ?.toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) != null;

  const initialRecipient = params.recipient || draft.recipient || "";
  const [isValidInput, setIsValidInput] = useState(() =>
    recipientLooksValid(initialRecipient)
  );
  const [amount, setAmount] = useState(params.amount || draft.amount || "");
  const [recipient, setRecipient] = useState(initialRecipient);
  const [chosenCard, setChosenCard] = useState(draft.chosenCard || cards[0]);
  const [chosenAccount, setChosenAccount] = useState(
    draft.chosenAccount || undefined
  );
  const [note, setNote] = useState(draft.note || "");
  const [repeatFrequency, setRepeatFrequency] = useState({
    label: "Never",
    value: "NEVER",
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Set initial recipient type based on interface mode
  const getInitialRecipientType = () => {
    if (p2pConfig?.p2p_interface_mode === 'FedNowDirectOnly') {
      return "bank_account";
    }
    return "phone_email";
  };
  const [recipientType, setRecipientType] = useState(
    draft.recipientType || getInitialRecipientType()
  );
  const [recipientName, setRecipientName] = useState(draft.recipientName || "");
  const [recipientEmail, setRecipientEmail] = useState(
    draft.recipientEmail || ""
  );
  const [routingNumber, setRoutingNumber] = useState(draft.routingNumber || "");
  const [accountNumber, setAccountNumber] = useState(draft.accountNumber || "");
  const [accountNumberConfirm, setAccountNumberConfirm] = useState(
    draft.accountNumberConfirm || ""
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmitFn, setPendingSubmitFn] = useState(null);

  const repeatFrequencyOptions = useMemo(
    () => [
      {
        label: "Never",
        value: "NEVER",
      },
      {
        label: "Weekly",
        value: "WEEKLY",
      },
      {
        label: "Monthly",
        value: "MONTHLY",
      },
    ],
    []
  );

  useEffect(() => {
    setFromDemoPage(false);
  });

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        draftKey,
        JSON.stringify({
          amount,
          recipient,
          note,
          recipientType,
          recipientName,
          recipientEmail,
          routingNumber,
          accountNumber,
          accountNumberConfirm,
          chosenCard,
          chosenAccount,
        })
      );
    } catch (e) {
      // storage unavailable — the form still works, it just won't survive navigation
    }
  }, [
    draftKey,
    amount,
    recipient,
    note,
    recipientType,
    recipientName,
    recipientEmail,
    routingNumber,
    accountNumber,
    accountNumberConfirm,
    chosenCard,
    chosenAccount,
  ]);


  useEffect(() => {
    if (getOS() === "macos" || getOS() === "ios" || isMobile()) {
      setDisableSendToPhone(false);
    }
  }, []);

  // Update recipientType when p2pConfig loads or changes
  useEffect(() => {
    if (p2pConfig?.p2p_interface_mode === 'FedNowDirectOnly') {
      setRecipientType("bank_account");
    } else if (p2pConfig?.p2p_interface_mode === 'P2POnly') {
      setRecipientType("phone_email");
    }
    // For 'Both' mode, keep user's selection
  }, [p2pConfig?.p2p_interface_mode]);

  useEffect(() => {
    if (loggedIn && accounts.length) {
      if (recipientType === "bank_account") {
        // For FedNow, use the chosenAccount directly
        if (chosenAccount) {
          const account = accounts.find(acc => acc.id === chosenAccount.value);
          console.log("Setting fromAccount for bank_account:", account);
          setFromAccount(account);
        } else {
          // If no chosenAccount yet, default to first account
          const ckAccount = accounts.find(acc => acc.code === "CK");
          const defaultAccount = ckAccount || accounts[0];
          console.log("No chosenAccount, defaulting to:", defaultAccount);
          setFromAccount(defaultAccount);
      }
      } else {
        // For phone/email
        // If rail_type='account', use chosenAccount (no cards)
        // Otherwise, use card-based account selection
        if (p2pConfig.rail_type === 'account') {
          if (chosenAccount) {
            const account = accounts.find(acc => acc.id === chosenAccount.value);
            console.log("Setting fromAccount for rail_type=account with phone/email:", account);
            setFromAccount(account);
          } else {
            // If no chosenAccount yet, default to first account
            const ckAccount = accounts.find(acc => acc.code === "CK");
            const defaultAccount = ckAccount || accounts[0];
            console.log("No chosenAccount, defaulting to:", defaultAccount);
            setFromAccount(defaultAccount);
          }
        } else {
          // For phone/email with cards, the from account depends on the card chosen
          // we want to set the account to the CK account if it exists, otherwise use the other/SV account
          let fromAccount = accounts
            .filter((account) =>
              account.card_uuids.includes(chosenCard?.value)
            )
            .sort((a, b) => a.code.localeCompare(b.code)) // sort by code, alphabetically (so CK is before SV)
            [0];
          setFromAccount(fromAccount);
        }
      }
    }
  }, [loggedIn, accounts, chosenCard, chosenAccount, recipientType, p2pConfig.rail_type]);


  // on cards load set the chosen card (keeping a drafted choice if that card
  // is still in the list)
  useEffect(() => {
    if (cards.length) {
      const drafted = cards.find((card) => card.uuid === draft.chosenCard?.value);
      const card = drafted || cards[0];
      setChosenCard({
        label: card.display_label,
        value: card.uuid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  // on accounts load set the chosen account (for FedNow)
  useEffect(() => {
    if (accounts.length && !chosenAccount) {
      // Default to checking account if available, otherwise first account
      const ckAccount = accounts.find(acc => acc.code === "CK");
      const defaultAccount = ckAccount || accounts[0];
      // Mask account number - show last 4 digits from dda field
      const accountNumber = defaultAccount.dda || "";
      const cleanAccountNumber = accountNumber.includes("_")
        ? accountNumber.split("_").pop()
        : accountNumber;
      const masked = cleanAccountNumber.length > 4
        ? `****${cleanAccountNumber.slice(-4)}`
        : cleanAccountNumber;
      const accountLabel = `${defaultAccount.code === "CK" ? "Checking" : "Savings"} ${masked}`;
      setChosenAccount({
        label: accountLabel,
        value: defaultAccount.id,
      });
    }
  }, [accounts, chosenAccount]);

  const validateRecipient = () => {
    const recipientType = recipient.includes("@") ? "email" : "phone";

    let recipientValue = recipient.trim().toLowerCase();
    if (recipientType === "phone") {
      if (disableSendToPhone) {
        return [];
      }

      const parsed = phone(recipientValue, { country: "USA" });
      if (!parsed.isValid) {
        return [];
      }
      recipientValue = parsed.phoneNumber;
    } else if (recipientType === "email") {
      const e =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!e.test(recipientValue)) {
        return [];
      }
    }
    return [{ type: recipientType, value: recipientValue }];
  };

  const validateAmount = () => {
    let newError = false;

    const amtVal = parseFloat(amount);
    if (amtVal < 0.0) {
      newError = true;
    }

    // return false = valid, no errors
    return !newError;
  };

  const handleRecipientChange = ({ target: { value } }) => {
    setRecipient(value ? value : "");
    if (
      (value.match(/^[0-9, +, \-, (, )]+$/) != null && value.length > 9) ||
      value
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      setIsValidInput(true);
    } else {
      setIsValidInput(false);
    }
  };

  useEffect(() => {
    if (
      (params.recipient?.match(/^[0-9, +, \-, (, )]+$/) != null &&
        params.recipient?.length > 9) ||
      params.recipient
        ?.toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      setIsValidInput(true);
    }
    // we do not need params in this dependency array, ignore warning
    // eslint-disable-next-line
  }, []);

  // The amount field is a text input (so it can size to its content and keep
  // the "$" tight against the digits); accept digits and at most one decimal
  // point with two places, which is what the old type="number" input allowed
  // in practice.
  const handleAmountChange = ({ target: { value } }) => {
    const cleaned = value.replace(/[^\d.]/g, "");
    const match = cleaned.match(/^\d*(\.\d{0,2})?/);
    setAmount(match ? match[0] : "");
  };

  const flashError = (e) => {
    setErrorMessage(e);
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 5000);
  };

  const closeScheduledAlert = () => {
    setScheduledAlert(false);
  };

  const fixScheduledAlert = (e) => {
    e.preventDefault();
    console.log("fix alert");
    // TODO handle scheduled alert issue
  };

  const clickClock = (e) => {
    e.preventDefault();
    navigate(SCHEDULE_PAYMENT);
  };

  const formatDate = (date) => {
    const formatStartDate = new Date(date);
    const year = formatStartDate.getFullYear();
    const month = String(formatStartDate.getMonth() + 1).padStart(2, "0");
    const day = String(formatStartDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isValidRouting = (rn) => {
    if (!/^\d{9}$/.test(rn)) return false;
    const digits = rn.split("").map((d) => parseInt(d, 10));
    const sum =
      3 * (digits[0] + digits[3] + digits[6]) +
      7 * (digits[1] + digits[4] + digits[7]) +
      1 * (digits[2] + digits[5] + digits[8]);
    return sum % 10 === 0;
  };

  const isValidAccountNumber = (an) => /^\d{4,17}$/.test(an);

  const isValidEmail = (email) => {
    if (!email) return true; // Email is optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidBankTransfer = () => {
    return (
      recipientName.trim() &&
      isValidRouting(routingNumber) &&
      isValidAccountNumber(accountNumber) &&
      accountNumber === accountNumberConfirm &&
      isValidEmail(recipientEmail) &&
      validateAmount()
    );
  };

  // Build a human-readable label for the source account/card shown in the confirmation modal.
  // The backend already returns masked account/card identifiers — no additional masking needed.
  const buildFromLabel = () => {
    if (recipientType === "bank_account" || p2pConfig?.rail_type === "account") {
      return fromAccount?.name || chosenAccount?.label || "Selected Account";
    }
    return chosenCard?.label || "Selected Card";
  };

  // Build the summary object passed to the confirmation modal.
  const buildTransferSummary = (recipientDisplay) => ({
    recipient: recipientDisplay,
    amount,
    note: note.trim(),
    fromLabel: buildFromLabel(),
    isScheduled: !!isScheduled,
    scheduledDate: isScheduled && startDate ? formatDate(startDate) : null,
    repeatFrequency: isScheduled ? repeatFrequency?.label : null,
  });

  const handleRequest = async (e) => {
    e.preventDefault();
    // Request for Pay — phone/email recipients only. Skips the amount/note
    // confirmation flow and goes straight to the backend, which branches
    // on-network (GetStream notification) vs off-network (shortlink) server-side.
    setIsSubmitted(true);
    // Strip leading/trailing whitespace from the recipient input before any
    // downstream code reads it. validateRecipient already trims internally,
    // but this also cleans the input box display + the disabled-state /
    // fallback paths that read raw `recipient`.
    const trimmedRecipient = recipient.trim();
    if (trimmedRecipient !== recipient) {
      setRecipient(trimmedRecipient);
    }
    try {
      const wrappedRecipient = validateRecipient();
      if (wrappedRecipient.length < 1) {
        flashError(
          disableSendToPhone
            ? "Invalid email"
            : "Invalid email or phone number"
        );
        return;
      }
      if (!validateAmount()) {
        flashError("Invalid amount");
        return;
      }
      if (!chosenCard?.value) {
        flashError("Please select a card to receive the payment");
        return;
      }
      if (!fromAccount?.id) {
        console.error("handleRequest: fromAccount not set");
        flashError("No funding account selected. Please refresh and try again.");
        return;
      }

      const requestData = {
        account: fromAccount.id,
        amount: { type: "USD", value: amount },
        card_uuid: chosenCard.value,
        note: note.trim(),
        recipient: wrappedRecipient,
      };

      const response = await api.post("request/outgoing", requestData);
      clearDraft();
      navigate(`${TRANSFER_PROCESSING.replace(":id", response.data.id)}`);
    } catch (error) {
      console.error("Request for Pay error", error);
      if (error.response?.status === 500 || error.response?.status === 400) {
        flashError(error.response?.data?.detail);
      } else {
        flashError("An error occurred while creating your request.");
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleSubmit = async (e) => {
    setIsSubmitted(true);
    e.preventDefault();

    // Strip leading/trailing whitespace from the recipient input before any
    // downstream code reads it. validateRecipient already trims internally,
    // but this also cleans the input box display + the disabled-state /
    // fallback paths that read raw `recipient`.
    const trimmedRecipient = recipient.trim();
    if (trimmedRecipient !== recipient) {
      setRecipient(trimmedRecipient);
    }

    try {
      if (recipientType === "bank_account") {
        // Bank account (FedNow) transfer validation
        if (!fromAccount) {
          flashError("Please select an account");
          return;
        }
        if (!recipientName.trim()) {
          flashError("Please enter recipient name");
          return;
        }
        if (!validateAmount()) {
          flashError("Invalid amount");
          return;
        }
        if (!isValidRouting(routingNumber)) {
          flashError("Invalid routing number");
          return;
        }
        if (!isValidAccountNumber(accountNumber)) {
          flashError("Invalid account number");
          return;
        }
        if (accountNumber !== accountNumberConfirm) {
          flashError("Account numbers do not match");
          return;
        }
        if (!isValidEmail(recipientEmail)) {
          flashError("Invalid email address");
          return;
        }

        const transferData = {
          account: fromAccount.id,
          amount: { type: "USD", value: amount },
          note: note.trim(),
          routing_number: routingNumber,
          account_number: accountNumber,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim() || undefined,
          recipient: [], // Backend expects this even for FedNow transfers
        };

        const executeTransfer = async () => {
          console.log("FedNow Transfer Data:", transferData);
          const response = await api.post("transfer/outgoing", transferData);
          clearDraft();
          navigate(`${TRANSFER_PROCESSING.replace(":id", response.data.id)}`);
        };

        if (branding?.show_confirmation_page) {
          setPendingSubmitFn(() => executeTransfer);
          setShowConfirmModal(true);
          setIsSubmitted(false);
          return;
        }

        await executeTransfer();
      } else {
        // Phone/email transfer
        const wrappedRecipient = validateRecipient();
        if (wrappedRecipient.length < 1) {
          let errMsg = "Invalid email";
          if (!disableSendToPhone) {
            errMsg = "Invalid email or phone number";
          }
          flashError(errMsg);
          return;
        }
        if (!validateAmount()) {
          flashError("Invalid amount");
          return;
        }

        if (!isScheduled) {
          const transferData = {
            account: fromAccount.id,
            amount: { type: "USD", value: amount },
            card_uuid: chosenCard.value,
            note: note.trim(),
            recipient: wrappedRecipient,
          };

          const executeTransfer = async () => {
            const response = await api.post("transfer/outgoing", transferData);
            clearDraft();
            navigate(`${TRANSFER_PROCESSING.replace(":id", response.data.id)}`);
          };

          if (branding?.show_confirmation_page) {
            const recipientDisplay = wrappedRecipient[0]?.value || recipient;
            setPendingSubmitFn(() => executeTransfer);
            setShowConfirmModal(true);
            setIsSubmitted(false);
            return;
          }

          await executeTransfer();
        } else {
          const oneMinuteFromNowLocalDateTime = new Date(
            new Date().getTime() + 1 * 60 * 1000
          );

          const oneMinuteFromNowUTCTimeOnly =
            "T" +
            (
              oneMinuteFromNowLocalDateTime.toISOString().slice(0, -1) + "Z"
            ).split("T")[1];

          let scheduledTransferData = {
            ...(p2pConfig?.rail_type !== "account" &&
              chosenCard?.value && { from_card: chosenCard.value }),
            from_account: fromAccount.id,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
            repeat_frequency: repeatFrequency.value,
            amount: { type: "USD", value: amount },
            recipient: wrappedRecipient[0],
            comment: note.trim(),
          };

          if (repeatFrequency.value === "NEVER") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence: startDate + oneMinuteFromNowUTCTimeOnly,
              end_date: null,
            };
          } else if (repeatFrequency.value === "WEEKLY") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence:
                new Date(
                  oneMinuteFromNowLocalDateTime.getTime() +
                    7 * 24 * 60 * 60 * 1000
                )
                  .toISOString()
                  .slice(0, -1) + "Z",
            };
          } else if (repeatFrequency.value === "MONTHLY") {
            scheduledTransferData = {
              ...scheduledTransferData,
              next_occurrence:
                new Date(
                  oneMinuteFromNowLocalDateTime.getFullYear(),
                  oneMinuteFromNowLocalDateTime.getMonth() + 1,
                  oneMinuteFromNowLocalDateTime.getDate()
                )
                  .toISOString()
                  .slice(0, -1) + "Z",
            };
          }

          if (!recipient.includes("@")) {
            flashError(
              "Scheduled transfers are currently only available to email recipients."
            );
            return;
          }

          const executeScheduledTransfer = async () => {
            const response = await schedulerApi.post(
              "schedules",
              scheduledTransferData
            );
            getScheduledTransfers();
            clearDraft();
            navigate(`${SCHEDULED_DETAILS.replace(":id", response.data.id)}`);
          };

          if (branding?.show_confirmation_page) {
            setPendingSubmitFn(() => executeScheduledTransfer);
            setShowConfirmModal(true);
            setIsSubmitted(false);
            return;
          }

          await executeScheduledTransfer();
        }
      }
    } catch (error) {
      console.error("Send money error", error);
      if (error.response?.status === 500 || error.response?.status === 400) {
        flashError(error.response?.data?.detail);
      } else {
        console.error("An unknown error occurred");
        flashError("An error occurred while sending these funds.");
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleConfirmModalConfirm = async () => {
    setShowConfirmModal(false);
    if (!pendingSubmitFn) return;
    setIsSubmitted(true);
    try {
      await pendingSubmitFn();
    } catch (error) {
      console.error("Send money error", error);
      if (error.response?.status === 500 || error.response?.status === 400) {
        flashError(error.response?.data?.detail);
      } else {
        console.error("An unknown error occurred");
        flashError("An error occurred while sending these funds.");
      }
    } finally {
      setIsSubmitted(false);
      setPendingSubmitFn(null);
    }
  };

  const handleConfirmModalCancel = () => {
    setShowConfirmModal(false);
    setPendingSubmitFn(null);
  };

  const today = new Date();
  const dateToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const twoDaysFromNow = new Date();
  twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 3);
  const dateTwoDaysFromNow = twoDaysFromNow?.toISOString()?.split("T")[0];

  const oneYearFromToday = new Date();
  oneYearFromToday.setDate(oneYearFromToday.getDate() + 366);
  const dateOneYearFromToday = oneYearFromToday?.toISOString()?.split("T")[0];

  const tenYearsFromToday = new Date();
  tenYearsFromToday.setFullYear(tenYearsFromToday.getFullYear() + 10);
  const dateTenYearsFromToday = formatDate(tenYearsFromToday);

  const accentColor =
    branding?.sending_colors?.sending_bg_secondary || "#1d74ec";

  const fromLabel =
    recipientType === "bank_account" || p2pConfig.rail_type === "account"
      ? "From Account"
      : "From";

  const chevron = (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );

  // Source selector: react-select inside a disclosure-style row. For
  // rail_type='account' with a phone/email recipient we show accounts instead
  // of cards; bank-account (FedNow) transfers always draw from an account.
  const fromRow = () =>
    recipientType === "phone_email" && p2pConfig.rail_type !== "account" ? (
      <div className="np-row np-row-select">
        <label htmlFor="select_card">{fromLabel}</label>
        <div className="np-row-field">
          <Select
            id="select_card"
            options={cards.map((card) => ({
              label: card.display_label,
              value: card.uuid,
            }))}
            value={chosenCard}
            onChange={(option) => setChosenCard(option)}
            classNamePrefix="dropdown"
            // menuIsOpen={true} // use for styling dev
            styles={dropDownStyle}
            theme={dropDownTheme}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>
    ) : (
      <div className="np-row np-row-select">
        <label htmlFor="select_account">{fromLabel}</label>
        <div className="np-row-field">
          <Select
            id="select_account"
            options={accounts.map((account) => {
              // Mask account number - show last 4 digits from dda field
              const accountNumber = account.dda || "";
              const cleanAccountNumber = accountNumber.includes("_")
                ? accountNumber.split("_").pop()
                : accountNumber;
              const masked =
                cleanAccountNumber.length > 4
                  ? `****${cleanAccountNumber.slice(-4)}`
                  : cleanAccountNumber;
              return {
                label: `${account.code === "CK" ? "Checking" : "Savings"} ${masked}`,
                value: account.id,
              };
            })}
            value={chosenAccount}
            onChange={(option) => setChosenAccount(option)}
            classNamePrefix="dropdown"
            styles={dropDownStyle}
            theme={dropDownTheme}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>
    );

  return (
    <>
      <form
        onSubmit={(e) => handleSubmit(e)}
        noValidate={true}
        autoComplete="off"
        className="page_without_nav np-send-form"
      >
        {initialDataLoading ? (
          <div className="center_page">
            <SpinnerCircularFixed
              size={60}
              thickness={180}
              speed={150}
              color={accentColor}
              secondaryColor="rgba(22, 24, 28, 0.08)"
            />
          </div>
        ) : accounts.length &&
          (p2pConfig.rail_type === "account" || cards.length) ? (
          <div>
            {scheduledAlert && (
              <div className="alert_message" role="status">
                <div style={{ display: "flex", gap: "10px" }}>
                  <span className="np-avatar np-avatar-muted" aria-hidden="true">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  <div style={{ textWrap: "balance" }}>
                    There was an issue with a scheduled payment
                    <button
                      type="button"
                      className="alert_message"
                      onClick={(e) => fixScheduledAlert(e)}
                    >
                      Fix the Payment
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  className="np-icon-btn"
                  onClick={() => closeScheduledAlert()}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Amount */}
            <div className="np-amount-card">
              <label htmlFor="amount" className="np-amount-caption">
                Amount
              </label>
              <div className="np-amount-entry">
                <span className="np-amount-dollar" aria-hidden="true">
                  $
                </span>
                <input
                  id="amount"
                  value={amount}
                  type="text"
                  inputMode="decimal"
                  size="4"
                  onChange={handleAmountChange}
                  onClick={() => validateRecipient()}
                  placeholder="0"
                  aria-label="Amount"
                />
              </div>
            </div>

            <RecipientTypeSelector
              selectedType={recipientType}
              onTypeChange={setRecipientType}
              showBankOption={
                (p2pConfig.p2p_interface_mode === "FedNowDirectOnly" ||
                  p2pConfig.p2p_interface_mode === "Both") &&
                !isScheduled
              }
              disableSendToPhone={disableSendToPhone}
              interfaceMode={p2pConfig.p2p_interface_mode}
            />

            {/* Recipient, source and note */}
            <div className="np-card np-send-card">
              {recipientType === "phone_email" ? (
                <div className="np-row">
                  <label htmlFor="recipient">To</label>
                  <input
                    id="recipient"
                    value={recipient}
                    placeholder={
                      disableSendToPhone || isScheduled
                        ? "Email"
                        : "Phone number or email"
                    }
                    onChange={handleRecipientChange}
                  />
                </div>
              ) : (
                <>
                  <div className="np-row">
                    <label htmlFor="recipient_name">Name</label>
                    <input
                      id="recipient_name"
                      value={recipientName}
                      placeholder="Enter recipient's name"
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                  </div>
                  <div className="np-row">
                    <label htmlFor="recipient_email">Email</label>
                    <input
                      id="recipient_email"
                      type="email"
                      value={recipientEmail}
                      placeholder="recipient@example.com"
                      onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                  </div>
                  <div className="np-row">
                    <label htmlFor="routing_number">Routing</label>
                    <input
                      id="routing_number"
                      value={routingNumber}
                      placeholder="9-digit routing number"
                      inputMode="numeric"
                      onChange={(e) =>
                        setRoutingNumber(
                          e.target.value.replace(/[^\d]/g, "").slice(0, 9)
                        )
                      }
                    />
                  </div>
                  <div className="np-row">
                    <label htmlFor="account_number">Account</label>
                    <input
                      id="account_number"
                      value={accountNumber}
                      placeholder="Account number"
                      inputMode="numeric"
                      onChange={(e) =>
                        setAccountNumber(
                          e.target.value.replace(/[^\d]/g, "").slice(0, 17)
                        )
                      }
                    />
                  </div>
                  <div className="np-row">
                    <label htmlFor="account_number_confirm">Confirm</label>
                    <input
                      id="account_number_confirm"
                      value={accountNumberConfirm}
                      placeholder="Re-enter account number"
                      inputMode="numeric"
                      onChange={(e) =>
                        setAccountNumberConfirm(
                          e.target.value.replace(/[^\d]/g, "").slice(0, 17)
                        )
                      }
                    />
                  </div>
                </>
              )}

              {fromRow()}

              <div className="np-row">
                <label htmlFor="note">Note</label>
                <input
                  id="note"
                  value={note}
                  placeholder="What's this for?"
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>

              {isScheduled && (
                <>
                  <div className="np-row">
                    <label htmlFor="start_date">Scheduled date</label>
                    <DatePicker
                      id="start_date"
                      placeholderText="Select scheduled date"
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      minDate={dateTwoDaysFromNow}
                      maxDate={dateOneYearFromToday}
                    />
                    <span className="np-row-chevron" aria-hidden="true">
                      {chevron}
                    </span>
                  </div>
                  <div className="np-row np-row-select">
                    <label htmlFor="repeat_frequency">Repeat</label>
                    <div className="np-row-field">
                      <Select
                        id="repeat_frequency"
                        options={repeatFrequencyOptions}
                        value={repeatFrequency}
                        onChange={(option) => setRepeatFrequency(option)}
                        classNamePrefix="dropdown"
                        // menuIsOpen={true} // use for styling dev
                        styles={dropDownStyle}
                        theme={dropDownTheme}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                  {repeatFrequency.value !== "NEVER" && (
                    <div className="np-row">
                      <label htmlFor="end_date">End date</label>
                      <DatePicker
                        id="end_date"
                        placeholderText="Select end date"
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        minDate={dateTwoDaysFromNow}
                        maxDate={dateTenYearsFromToday}
                      />
                      <span className="np-row-chevron" aria-hidden="true">
                        {chevron}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {errorMessage && (
              <div className="error_message" role="alert">
                {errorMessage}
              </div>
            )}

            {isScheduled ? (
              <div className="np-actions" style={{ marginTop: "26px" }}>
                <button
                  disabled={
                    !amount ||
                    !recipient ||
                    isSubmitted ||
                    !isValidInput ||
                    !startDate ||
                    startDate < dateToday ||
                    (repeatFrequency.value !== "NEVER" && !endDate) ||
                    (repeatFrequency.value !== "NEVER" && startDate >= endDate)
                  }
                  type="submit"
                  data-cy="save_schedule_button"
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="np-actions" style={{ marginTop: "26px" }}>
                <div className="np-actions-row">
                  {p2pConfig.scheduled_recurring_enabled && (
                    <button
                      type="button"
                      onClick={(e) => clickClock(e)}
                      className="np-btn-icon"
                      aria-label="Schedule a payment"
                      data-cy="clock_button"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <polyline points="12 7 12 12 15.5 14" />
                      </svg>
                    </button>
                  )}
                  <button
                    disabled={
                      recipientType === "phone_email"
                        ? !amount || !recipient || isSubmitted || !isValidInput
                        : !isValidBankTransfer() || isSubmitted
                    }
                    type="submit"
                    data-cy="send_money_button"
                  >
                    {recipientType === "bank_account"
                      ? "Send to account"
                      : "Send payment now"}
                  </button>
                </div>
                {p2pConfig?.request_for_pay &&
                  recipientType === "phone_email" && (
                    <button
                      type="button"
                      onClick={handleRequest}
                      disabled={
                        !amount || !recipient || isSubmitted || !isValidInput
                      }
                      className="np-btn-secondary"
                      data-cy="request_money_button"
                    >
                      Request payment instead
                    </button>
                  )}
              </div>
            )}
          </div>
        ) : (
          <div className="center_page">
            <div>
              <h3>
                You are not currently enabled to use this service. This could
                be for a variety of reasons including not having an eligible
                account or an active card.
              </h3>
              <h4>
                If you believe you have received this by mistake, please
                contact customer support.
              </h4>
            </div>
          </div>
        )}

        {/* Full-page busy overlay while a submit is in flight */}
        {isSubmitted && (
          <div
            className="np-dialog-backdrop"
            style={{ zIndex: 9999 }}
            role="status"
            aria-live="polite"
          >
            <div className="np-busy">
              <SpinnerCircularFixed
                size={60}
                thickness={180}
                speed={150}
                color={accentColor}
                secondaryColor="rgba(22, 24, 28, 0.08)"
              />
              <p>
                {recipientType === "bank_account"
                  ? "Verifying routing number capabilities..."
                  : "Processing..."}
              </p>
            </div>
          </div>
        )}
      </form>
      {showConfirmModal && (
        <ConfirmTransferModal
          transferSummary={buildTransferSummary(
            recipientType === "bank_account"
              ? recipientName.trim() || "Recipient"
              : recipient
          )}
          onConfirm={handleConfirmModalConfirm}
          onCancel={handleConfirmModalCancel}
        />
      )}
    </>
  );
};

export default SendMoney;
